/**
 * Phase 5.16 — Pre-upload inspection modal.
 *
 * Para cada archivo que el usuario seleccione, mostramos:
 *   - tipo detectado + filas
 *   - rango de fechas (caja azul calendario)
 *   - metadata según tipo
 *   - estrategia (replace_period [recomendado] vs replace_all)
 *   - avisos (>2 meses, dataset pequeño, ambiguous_type)
 *   - botones: Omitir / Cancelar todo / Cargar
 *
 * Props:
 *   open: boolean
 *   files: File[] (cola, índice activo = 0)
 *   onClose(): cierra todo
 *   onIngestComplete(results): se llama tras procesar TODA la cola
 *
 * Estado interno: por archivo carga `/inspect`, espera decisión, llama
 * `/excel/ingest` o salta. Acumula resultados y al final llama onIngestComplete.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  Calendar,
  Database,
  FileSpreadsheet,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";

const formatDateEs = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString("es-MX") : n ?? "—";

const METADATA_LABEL = (rt, meta) => {
  if (!meta) return null;
  if (rt === "GENDETAILED_PROD") {
    return `${fmt(meta.employees_count)} empleado(s) · ${fmt(
      meta.transaction_types_count
    )} tipo(s) RF · ${fmt(meta.unique_items)} SKU(s)`;
  }
  if (rt === "IC385DETAIL" || rt === "IC385P2DETAIL") {
    return `${fmt(meta.employees_count)} operator(es) · ${fmt(
      meta.transaction_types_count
    )} tipo(s) de transacción · ${fmt(meta.unique_items)} producto(s)`;
  }
  if (rt === "TE350") {
    // Phase 9.5 unified TE350 through parse_time_clock; the metadata key is
    // now `total_punch_hours`. Fall back to legacy `total_hours` so the
    // label survives if either source ships that.
    const hours = meta.total_punch_hours ?? meta.total_hours;
    return `${fmt(meta.employees_count)} empleado(s) · ${fmt(hours)} horas totales`;
  }
  if (rt === "TIME_CLOCK_MONTH" || rt === "TIME_CLOCK_YEARLY") {
    return `${fmt(meta.employees_count)} empleado(s) · ${fmt(
      meta.declared_totals_count
    )} totales declarados`;
  }
  if (rt === "SCHEDULE") {
    return `${fmt(meta.weeks_detected)} semana(s) · ${fmt(
      meta.employees_count
    )} empleado(s) · ${fmt(meta.entries_count)} turnos`;
  }
  if (rt === "SHOPIFY_CSV") {
    return `${fmt(meta.unique_skus)} SKU(s) · ${fmt(
      meta.unique_handles
    )} handle(s) · ${fmt(meta.unique_vendors)} vendor(es)`;
  }
  if (rt === "IC338") {
    return `${fmt(meta.unique_skus)} SKU(s)`;
  }
  if (rt === "GENQTY_AVAIL") {
    return `${fmt(meta.unique_skus)} SKU(s) · ${fmt(meta.unique_vendors)} vendor(es)`;
  }
  return null;
};

const WARNING_MESSAGES = {
  file_covers_more_than_2_months:
    "El archivo cubre más de 2 meses. Reemplazará todo ese período de una sola vez.",
  file_covers_more_than_1_year:
    "El archivo abarca más de un año. Verifica que esto sea lo que quieres.",
  very_small_dataset:
    "Archivo pequeño (<100 filas). ¿Es un dataset parcial?",
  ambiguous_type:
    "El nombre del archivo sugiere un tipo distinto al contenido detectado. Revisa antes de cargar.",
  unsupported_type:
    "Tipo de archivo no soportado. Revisa el nombre o usa un Excel válido.",
  parse_failed: "El parser no pudo leer el archivo. ¿Está corrupto?",
};

const CATALOG_TYPES = new Set(["SHOPIFY_CSV", "IC338", "GENQTY_AVAIL"]);

const strategyDesc = ({ value, reportType, dateFrom, dateTo, skusCount }) => {
  const isCatalog = CATALOG_TYPES.has(reportType);
  const skusLabel = skusCount != null ? ` (${fmt(skusCount)} SKUs)` : "";
  if (value === "replace_period") {
    if (isCatalog) {
      return `Borra solo los SKUs incluidos en este archivo${skusLabel} y los reemplaza. Los demás SKUs del catálogo quedan intactos.`;
    }
    return `Borra solo los registros entre ${formatDateEs(dateFrom)} y ${formatDateEs(
      dateTo,
    )} y los reemplaza con este archivo. El histórico fuera de ese rango queda intacto.`;
  }
  if (value === "replace_all") {
    return `Borra todos los registros previos de ${reportType} y los reemplaza con este archivo. Útil sólo si quieres rehacer el dataset desde cero.`;
  }
  return "";
};

export default function UploadInspectModal({ open, files, onClose, onIngestComplete }) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [inspect, setInspect] = useState(null);
  const [inspecting, setInspecting] = useState(false);
  const [inspectError, setInspectError] = useState(null);
  const [strategy, setStrategy] = useState("replace_period");
  const [ingesting, setIngesting] = useState(false);
  const [results, setResults] = useState([]);

  const total = files?.length || 0;
  const file = files?.[idx];

  // Reset al abrir o cambiar de archivo activo. Inspect del archivo activo:
  // POST /uploads/inspect → { report_type, rows, date_range, metadata, ... }.
  useEffect(() => {
    if (!open || !file) return undefined;
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      setInspect(null);
      setInspectError(null);
      setStrategy("replace_period");
      setInspecting(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/inspect", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (cancelled) return;
        setInspect(data);
        if (Array.isArray(data?.strategy_options) && data.strategy_options.length === 1) {
          setStrategy(data.strategy_options[0]);
        } else {
          setStrategy(data?.suggested_strategy || "replace_period");
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object") {
          setInspect(detail);
          const w = (detail.warnings || [])[0];
          setInspectError(WARNING_MESSAGES[w] || detail.error || formatApiError(err));
        } else {
          setInspectError(formatApiError(err));
        }
      } finally {
        if (!cancelled) setInspecting(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, file, idx]);

  // Reinicia el cursor (idx) y los resultados al abrir el modal.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      setIdx(0);
      setResults([]);
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  const proceed = (nextIdx) => {
    if (nextIdx >= total) {
      onIngestComplete?.(results);
      onClose?.();
      return;
    }
    setIdx(nextIdx);
  };

  const onSkip = () => {
    proceed(idx + 1);
  };

  const onCancelAll = () => {
    onClose?.();
  };

  const onIngest = async () => {
    if (!file || !inspect?.report_type) return;
    setIngesting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("report_type", inspect.report_type);
      fd.append("strategy", strategy);
      fd.append("confirmed", "true");
      // Phase 8.6 — Time Clock filter opt-in (default true).
      if (isTimeClock) {
        fd.append(
          "only_with_transactions",
          timeClockFilterWithTx ? "true" : "false",
        );
      }
      // Catálogo: tiene endpoints separados, no usamos /excel/ingest
      // pero el patrón es uniforme: el endpoint actual replace_all por default.
      let endpoint = "/excel/ingest";
      if (inspect.report_type === "SHOPIFY_CSV") endpoint = "/catalog/ingest/shopify";
      else if (inspect.report_type === "IC338") endpoint = "/catalog/ingest/ic338";
      else if (inspect.report_type === "GENQTY_AVAIL") endpoint = "/catalog/ingest/genqty-avail";
      else if (inspect.report_type === "SCHEDULE") endpoint = "/schedules";
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults((r) => [...r, { filename: file.name, data }]);
      const period = data?.replaced_period;
      // Phase 10.1 — build a "N SKUs preservados intactos" suffix for
      // catalog reports (SHOPIFY_CSV / IC338 / GENQTY_AVAIL). The backend
      // exposes `preserved` uniformly for both replace_period and update_qty
      // strategies. For non-catalog reports the field is absent and we omit
      // the sentence.
      const preserved = data?.preserved;
      const preservedSuffix =
        typeof preserved === "number" && preserved > 0
          ? ` · ${fmt(preserved)} SKUs preservados intactos`
          : "";
      if (data?.strategy === "update_qty") {
        // Phase 8.2 — detailed stats for the non-destructive merge.
        toast.success(
          t("upload.strategy.statsToast", {
            updated: fmt(data?.updated ?? 0),
            inserted: fmt(data?.inserted ?? 0),
            unchanged: fmt(data?.unchanged ?? 0),
            defaultValue: `Actualizados: ${fmt(data?.updated ?? 0)} · Nuevos: ${fmt(
              data?.inserted ?? 0
            )} · Sin cambios: ${fmt(data?.unchanged ?? 0)}`,
          }) + preservedSuffix
        );
      } else if (period?.from) {
        toast.success(
          `Se reemplazaron registros del ${formatDateEs(period.from)} al ${formatDateEs(
            period.to
          )} (${fmt(data?.deleted_old || 0)} viejos · ${fmt(
            data?.inserted ?? data?.time_entries_inserted ?? 0
          )} nuevos).` + preservedSuffix
        );
      } else {
        toast.success(
          `Cargado ${file.name} · ${fmt(
            data?.inserted ?? data?.time_entries_inserted ?? data?.rows_inserted ?? data?.skus_inserted ?? 0
          )} registros nuevos.` + preservedSuffix
        );
      }
      proceed(idx + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setIngesting(false);
    }
  };

  const warnings = useMemo(() => inspect?.warnings || [], [inspect]);
  const dateRange = inspect?.date_range || {};
  const metaLine = METADATA_LABEL(inspect?.report_type, inspect?.metadata);
  const strategyOptions = inspect?.strategy_options || ["replace_period", "replace_all"];
  // Phase 8.6 — Time Clock filter (only rows for employees with existing
  // transactions). Enabled by default; the user opts out explicitly if they
  // want to load ALL employees from the file. Only shown for TIME_CLOCK_*.
  const isTimeClock =
    inspect?.report_type === "TIME_CLOCK_MONTH" ||
    inspect?.report_type === "TIME_CLOCK_YEARLY";
  const [timeClockFilterWithTx, setTimeClockFilterWithTx] = useState(true);

  if (!open || !file) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancelAll()}>
      <DialogContent
        data-testid="upload-inspect-modal"
        className="max-w-2xl gap-0 p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div>
            <div
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
              data-testid="modal-step-label"
            >
              {total > 1
                ? `Archivo ${idx + 1} de ${total} · Antes de cargar`
                : "Antes de cargar"}
            </div>
            <h2
              className="mt-1 font-mono text-sm font-semibold"
              data-testid="modal-filename"
            >
              {file.name}
            </h2>
            <p className="text-xs text-muted-foreground">
              {(file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
          <button
            type="button"
            onClick={onCancelAll}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            data-testid="modal-close-btn"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-5">
          {inspecting ? (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              data-testid="inspect-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Inspeccionando archivo…
            </div>
          ) : inspectError && !inspect?.report_type ? (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-sm text-destructive"
              data-testid="inspect-error"
            >
              <AlertTriangle className="mr-2 inline h-4 w-4" />
              {inspectError}
            </div>
          ) : inspect ? (
            <>
              {/* Tipo + filas */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Tipo detectado
                  </Label>
                  <div
                    className="mt-1 flex items-center gap-2"
                    data-testid="modal-detected-type"
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-mono text-sm font-semibold">
                      {inspect.report_type || "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Filas
                  </Label>
                  <div
                    className="mt-1 font-mono text-sm font-semibold tabular-nums"
                    data-testid="modal-rows"
                  >
                    {fmt(inspect.rows)}
                  </div>
                </div>
              </div>

              {/* Período cubierto */}
              {(dateRange.from || dateRange.to) && (
                <div
                  className="rounded-md border border-sky-500/30 bg-sky-500/10 px-4 py-3"
                  data-testid="modal-date-range"
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-sky-700 dark:text-sky-300">
                    <Calendar className="h-3.5 w-3.5" />
                    Período cubierto por el archivo
                  </div>
                  <div className="mt-1 font-mono text-sm text-sky-900 dark:text-sky-100">
                    {formatDateEs(dateRange.from)} → {formatDateEs(dateRange.to)}
                    {dateRange.days ? (
                      <span className="ml-2 text-xs opacity-70">({dateRange.days} días)</span>
                    ) : null}
                  </div>
                  {metaLine && (
                    <div
                      className="mt-1 text-xs text-sky-800/80 dark:text-sky-200/80"
                      data-testid="modal-metadata"
                    >
                      {metaLine}
                    </div>
                  )}
                </div>
              )}

              {/* Si no hay fechas (catalog), muestra metadata sin caja azul */}
              {!dateRange.from && !dateRange.to && metaLine && (
                <div
                  className="rounded-md border border-border bg-muted/40 px-4 py-3 text-xs"
                  data-testid="modal-metadata"
                >
                  {metaLine}
                </div>
              )}

              {/* Avisos */}
              {warnings.length > 0 && (
                <div
                  className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3"
                  data-testid="modal-warnings"
                >
                  {warnings.map((w) => (
                    <div
                      key={w}
                      className="flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200"
                      data-testid={`modal-warning-${w}`}
                    >
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{WARNING_MESSAGES[w] || w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Estrategia */}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Cómo procesar
                </Label>
                <div className="mt-2 space-y-2" data-testid="strategy-options">
                  {strategyOptions.includes("replace_period") && (
                    <StrategyOption
                      value="replace_period"
                      current={strategy}
                      onChange={setStrategy}
                      label="Actualizar sólo este período"
                      badge="recomendado"
                      desc={strategyDesc({
                        value: "replace_period",
                        reportType: inspect.report_type,
                        dateFrom: dateRange.from,
                        dateTo: dateRange.to,
                        skusCount: inspect?.metadata?.unique_skus,
                      })}
                    />
                  )}
                  {strategyOptions.includes("replace_all") && (
                    <StrategyOption
                      value="replace_all"
                      current={strategy}
                      onChange={setStrategy}
                      label="Reemplazar TODO el reporte"
                      desc={strategyDesc({
                        value: "replace_all",
                        reportType: inspect.report_type,
                      })}
                      tone="destructive"
                    />
                  )}
                  {strategyOptions.includes("update_qty") && (
                    <StrategyOption
                      value="update_qty"
                      current={strategy}
                      onChange={setStrategy}
                      label={t("upload.strategy.updateQtyLabel", {
                        defaultValue: "Actualizar solo cantidades",
                      })}
                      badge={t("upload.strategy.updateQtyBadge", {
                        defaultValue: "seguro",
                      })}
                      desc={t("upload.strategy.updateQtyHelp", {
                        defaultValue:
                          "Solo actualiza qty y campos no vacíos. Los SKUs en DB que no estén en este Excel se conservan; las celdas en blanco no borran valores existentes.",
                      })}
                    />
                  )}
                </div>
              </div>

              {/* Phase 8.6 — Time Clock scope filter */}
              {isTimeClock && (
                <div data-testid="time-clock-filter-section">
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t("upload.strategy.timeClockScopeLabel", {
                      defaultValue: "Alcance del Time Clock",
                    })}
                  </Label>
                  <div className="mt-2 space-y-2">
                    <StrategyOption
                      value="only_with_tx"
                      current={timeClockFilterWithTx ? "only_with_tx" : "all"}
                      onChange={() => setTimeClockFilterWithTx(true)}
                      label={t("upload.strategy.timeClockWithTxLabel", {
                        defaultValue:
                          "Solo empleados con transacciones existentes",
                      })}
                      badge={t("upload.strategy.timeClockWithTxBadge", {
                        defaultValue: "recomendado",
                      })}
                      desc={t("upload.strategy.timeClockWithTxHelp", {
                        defaultValue:
                          "Ignora del Time Clock a las personas que no tengan ninguna transacción registrada (staff admin, gerentes, etc). Reporta cuántos se filtraron.",
                      })}
                    />
                    <StrategyOption
                      value="all"
                      current={timeClockFilterWithTx ? "only_with_tx" : "all"}
                      onChange={() => setTimeClockFilterWithTx(false)}
                      label={t("upload.strategy.timeClockAllLabel", {
                        defaultValue: "Cargar TODOS los empleados del archivo",
                      })}
                      desc={t("upload.strategy.timeClockAllHelp", {
                        defaultValue:
                          "Comportamiento tradicional. Puede incluir personal que nunca operó un scanner.",
                      })}
                    />
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-6 py-3">
          <button
            type="button"
            onClick={onSkip}
            disabled={ingesting}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            data-testid="modal-skip-btn"
          >
            Omitir este archivo
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCancelAll}
              disabled={ingesting}
              data-testid="modal-cancel-all-btn"
            >
              Cancelar todo
            </Button>
            <Button
              size="sm"
              onClick={onIngest}
              disabled={ingesting || !inspect?.report_type || inspecting}
              data-testid="modal-ingest-btn"
              className="min-w-[120px]"
            >
              {ingesting ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Cargando…
                </>
              ) : (
                <>
                  <Database className="mr-2 h-3.5 w-3.5" />
                  Cargar
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StrategyOption({ value, current, onChange, label, badge, desc, tone }) {
  const selected = value === current;
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 transition-colors ${
        selected
          ? tone === "destructive"
            ? "border-rose-500/60 bg-rose-500/10"
            : "border-emerald-500/60 bg-emerald-500/10"
          : "border-border bg-background hover:bg-accent/40"
      }`}
      data-testid={`strategy-option-${value}`}
    >
      <input
        type="radio"
        className="mt-1"
        checked={selected}
        onChange={() => onChange(value)}
        data-testid={`strategy-radio-${value}`}
      />
      <div className="flex-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          {label}
          {badge && (
            <Badge variant="outline" className="border-emerald-500/40 text-[10px] text-emerald-600 dark:text-emerald-300">
              {badge}
            </Badge>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>
      </div>
    </label>
  );
}
